import React, { useEffect, useState } from "react";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, X, ArrowRight, Mail, UserPlus, Copy, Check, Clock } from "lucide-react";
import PhotoUpload from "../components/app/PhotoUpload";
import AuthImage from "../components/app/AuthImage";
import { toast } from "sonner";

const blank = {
  name: "", age: 8, position: "", team: "", jersey_number: "",
  height_cm: "", weight_kg: "", dominant_foot: "", medical_notes: "",
  goals: 0, assists: 0, matches_played: 0, parent_id: "", photo_url: "",
};

export default function ChildrenPage() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blank);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/children");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form };
      ["age", "jersey_number", "height_cm", "weight_kg", "goals", "assists", "matches_played"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] == null ? null : Number(payload[k]);
      });
      if (!payload.parent_id) delete payload.parent_id;
      await api.post("/children", payload);
      setForm(blank);
      setShowForm(false);
      load();
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    }
  };

  // Iter 31 — Coaches & academies CANNOT manually create child profiles.
  // The roster must consist of real registered users (parents/players) who
  // book sessions or apply via the academy form. Only parents can add their
  // own children directly.
  const canAddPlayer = user?.role === "parent";

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-mono mb-1">// {t("players")}</div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">{t("children")}</h1>
            {!canAddPlayer && (
              <p className="text-xs text-white/45 font-mono mt-2 max-w-xl">
                {lang === "bg"
                  ? "Списъкът се пълни автоматично от реални играчи/родители, които резервират сесии или кандидатстват към твоя профил."
                  : "This roster is auto-populated by real players/parents who book sessions or apply to your profile."}
              </p>
            )}
          </div>
          {canAddPlayer && (
            <button onClick={() => setShowForm(true)} className="btn-primary px-5 py-2.5 flex items-center gap-2" data-testid="btn-add-child">
              <Plus className="w-4 h-4" /> {t("addChild")}
            </button>
          )}
        </div>

        {list.length === 0 ? (
          <div className="surface-elevated p-14 text-center rounded-lg" data-testid="empty-children">
            <div className="w-14 h-14 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center mx-auto mb-3">
              <Plus className="w-5 h-5 text-pitch" />
            </div>
            <div className="font-oswald uppercase text-xl tracking-tight">{t("noChildren")}</div>
            {canAddPlayer ? (
              <button onClick={() => setShowForm(true)} className="btn-primary px-5 py-2.5 text-xs mt-5 inline-flex items-center gap-2" data-testid="empty-add-child">
                <Plus className="w-3.5 h-3.5" /> {t("addChild")}
              </button>
            ) : (
              <p className="text-xs text-white/45 font-mono mt-3 max-w-md mx-auto leading-relaxed" data-testid="empty-coach-hint">
                {lang === "bg"
                  ? "Когато родител или играч резервира сесия или кандидатства към твоя профил, той ще се появи тук."
                  : "When a parent or player books a session or applies to your profile, they'll appear here."}
              </p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((c, i) => {
              if (c.invite_status === "pending") {
                return (
                  <InvitePendingCard
                    key={c.id}
                    child={c}
                    lang={lang}
                    onChanged={load}
                    style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}
                  />
                );
              }
              return (
              <Link
                to={c.is_player_user ? `/player/${c.id}` : `/children/${c.id}`}
                key={c.id}
                className="card-coach p-5 group animate-fade-up rounded-lg relative"
                style={{ animationDelay: `${i * 40}ms`, opacity: 0 }}
                data-testid={`child-${c.id}`}
              >
                {(c.is_player_user || c.invite_status === "claimed") && (
                  <span className="absolute top-2 right-2 chip-badge text-[9px] tracking-widest" data-testid={`player-user-tag-${c.id}`}>
                    {c.invite_status === "claimed"
                      ? (lang === "bg" ? "Свързан" : "Linked")
                      : (lang === "bg" ? "Профил" : "Profile")}
                  </span>
                )}
                <div className="flex items-center gap-4">
                  <div className="relative w-14 h-14 flex-shrink-0">
                    {c.photo_url ? (
                      <AuthImage
                        src={c.photo_url}
                        className="w-full h-full object-cover rounded-full border-2 border-pitch/40"
                        fallback={<div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-lg">{c.jersey_number || c.name?.[0]}</div>}
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-lg">
                        {c.jersey_number || c.name?.[0]}
                      </div>
                    )}
                    {c.position && (
                      <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#0A1628] border border-pitch/40 text-[9px] font-mono text-pitch flex items-center justify-center font-bold uppercase">
                        {c.position.slice(0, 2)}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-oswald uppercase text-lg tracking-tight truncate leading-tight">{c.name}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {c.age != null && <span className="text-xs font-mono text-white/45">{c.age} {lang === "bg" ? "г." : "yr"}</span>}
                      {c.team && <><span className="text-white/20">·</span><span className="text-xs font-mono text-white/55 truncate">{c.team}</span></>}
                      {c.jersey_number != null && <><span className="text-white/20">·</span><span className="text-xs font-mono text-pitch">#{c.jersey_number}</span></>}
                    </div>
                  </div>
                  <ArrowRight className="w-4 h-4 text-white/25 group-hover:text-pitch group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="mt-4 pt-3 border-t border-white/[0.06] grid grid-cols-3 gap-2">
                  <Stat label={lang === "bg" ? "Гола" : "Goals"} value={c.goals || 0} />
                  <Stat label={lang === "bg" ? "Асистенции" : "Assists"} value={c.assists || 0} />
                  <Stat label={lang === "bg" ? "Мачове" : "Matches"} value={c.matches_played || 0} />
                </div>
              </Link>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6" onClick={() => setShowForm(false)} data-testid="child-form-modal">
          <div className="bg-[#0A1628] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628] z-10">
              <h2 className="font-oswald uppercase text-2xl tracking-tight">{t("addChild")}</h2>
              <button onClick={() => setShowForm(false)} data-testid="close-form"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-5">
              <div className="flex items-start gap-5">
                <PhotoUpload
                  value={form.photo_url}
                  onChange={(url) => setForm({ ...form, photo_url: url })}
                  size={96}
                  testId="photo-upload"
                />
                <div className="flex-1 space-y-3">
                  <Field label={t("name")} v={form.name} on={(v) => setForm({ ...form, name: v })} required testId="field-name" />
                  <div className="grid grid-cols-2 gap-3">
                    <Field label={t("age")} type="number" v={form.age} on={(v) => setForm({ ...form, age: v })} required testId="field-age" />
                    <Field label={t("jerseyNumber")} type="number" v={form.jersey_number} on={(v) => setForm({ ...form, jersey_number: v })} testId="field-jersey" />
                  </div>
                </div>
              </div>

              <div>
                <div className="label-mono mb-3">// {t("basic")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("position")} v={form.position} on={(v) => setForm({ ...form, position: v })} testId="field-position" />
                  <Field label={t("team")} v={form.team} on={(v) => setForm({ ...form, team: v })} testId="field-team" />
                </div>
              </div>

              <div>
                <div className="label-mono mb-3">// {t("extended")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("height")} type="number" v={form.height_cm} on={(v) => setForm({ ...form, height_cm: v })} testId="field-height" />
                  <Field label={t("weight")} type="number" v={form.weight_kg} on={(v) => setForm({ ...form, weight_kg: v })} testId="field-weight" />
                  <Field label={t("dominantFoot")} v={form.dominant_foot} on={(v) => setForm({ ...form, dominant_foot: v })} testId="field-foot" />
                  <Field label={t("goals")} type="number" v={form.goals} on={(v) => setForm({ ...form, goals: v })} testId="field-goals" />
                  <Field label={t("assists")} type="number" v={form.assists} on={(v) => setForm({ ...form, assists: v })} testId="field-assists" />
                  <Field label={t("matchesPlayed")} type="number" v={form.matches_played} on={(v) => setForm({ ...form, matches_played: v })} testId="field-matches" />
                </div>
                <div className="mt-3">
                  <label className="label-mono block mb-1.5">{t("medicalNotes")}</label>
                  <textarea
                    value={form.medical_notes}
                    onChange={(e) => setForm({ ...form, medical_notes: e.target.value })}
                    rows={3}
                    className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
                    data-testid="field-medical"
                  />
                </div>
              </div>

              {err && <div className="text-red-400 text-sm font-mono">{err}</div>}

              <div className="flex gap-3 sticky bottom-0 bg-[#0A1628] pt-3 border-t border-white/10">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 btn-secondary py-3" data-testid="cancel-child">{t("cancel")}</button>
                <button type="submit" className="flex-1 btn-primary py-3" data-testid="save-child">{t("save")}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value }) {
  return (
    <div className="text-center">
      <div className="font-oswald text-pitch text-xl leading-none text-glow-emerald">{value}</div>
      <div className="text-[9px] font-mono text-white/35 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}

function Field({ label, v, on, type = "text", required, testId }) {
  return (
    <div>
      <label className="label-mono block mb-1.5">{label}</label>
      <input
        type={type}
        value={v ?? ""}
        onChange={(e) => on(e.target.value)}
        required={required}
        className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none"
        data-testid={testId}
      />
    </div>
  );
}


function InvitePendingCard({ child, lang, onChanged, style }) {
  const [copied, setCopied] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [inviteData, setInviteData] = useState(null);

  const reIssue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/children/${child.id}/invite`, { email: child.invite_email });
      setInviteData(data);
      setShowInvite(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const cancelInvite = async () => {
    if (!window.confirm(lang === "bg" ? "Да отменя ли поканата?" : "Cancel this invite?")) return;
    setBusy(true);
    try {
      await api.delete(`/children/${child.id}/invite`);
      toast.success(lang === "bg" ? "Поканата е отменена" : "Invite cancelled");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  const copyUrl = async () => {
    const base = window.location.origin;
    // We don't have the original token in `child` (security), so we re-issue
    // to get a fresh one (server treats this as refresh).
    if (!inviteData) {
      await reIssue();
      return;
    }
    try {
      await navigator.clipboard.writeText(inviteData.invite_url || `${base}/claim/${inviteData.invite_token}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {}
  };

  return (
    <div
      className="card-coach p-5 animate-fade-up rounded-lg relative border-2 border-dashed border-amber-300/35 bg-amber-300/[0.03]"
      style={style}
      data-testid={`child-pending-${child.id}`}
    >
      <span className="absolute top-2 right-2 chip-badge amber flex items-center gap-1 text-[9px]" data-testid={`pending-badge-${child.id}`}>
        <Clock className="w-2.5 h-2.5" /> {lang === "bg" ? "Изчаква покана" : "Pending invite"}
      </span>
      <div className="flex items-center gap-4">
        <div className="w-14 h-14 rounded-full bg-amber-500/10 border-2 border-amber-300/30 text-amber-300 font-oswald flex items-center justify-center text-lg flex-shrink-0">
          {child.name?.[0]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-oswald uppercase text-lg tracking-tight truncate leading-tight">{child.name}</div>
          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
            {child.age != null && <span className="text-xs font-mono text-white/45">{child.age} {lang === "bg" ? "г." : "yr"}</span>}
            {child.position && <><span className="text-white/20">·</span><span className="text-xs font-mono text-white/55 truncate">{child.position}</span></>}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5">
            <Mail className="w-3 h-3 text-amber-300/70" />
            <span className="text-[11px] font-mono text-white/60 truncate">{child.invite_email}</span>
          </div>
        </div>
      </div>
      <div className="mt-4 pt-3 border-t border-amber-300/15 flex gap-2">
        <button
          type="button"
          onClick={copyUrl}
          disabled={busy}
          className="flex-1 btn-secondary px-3 py-2 text-xs inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
          data-testid={`copy-invite-${child.id}`}
        >
          {copied ? <Check className="w-3 h-3 text-pitch" /> : <Copy className="w-3 h-3" />}
          {copied
            ? (lang === "bg" ? "Копирано" : "Copied")
            : (lang === "bg" ? "Копирай линк" : "Copy link")}
        </button>
        <button
          type="button"
          onClick={cancelInvite}
          disabled={busy}
          className="px-3 py-2 text-xs text-red-300 hover:bg-red-500/10 rounded-md transition-colors"
          data-testid={`cancel-invite-${child.id}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      {showInvite && inviteData?.invite_url && (
        <div className="mt-3 p-2 bg-black/40 border border-amber-300/20 rounded text-[10px] font-mono text-amber-300/85 break-all" data-testid={`invite-url-${child.id}`}>
          {inviteData.invite_url}
        </div>
      )}
    </div>
  );
}
